import type { AppRouter } from '@dentaly/server/trpc'
import { TRPCClientError } from '@trpc/client'
import type { inferRouterOutputs } from '@trpc/server'
import { ArrowLeft, Save } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { Textarea } from '@/components/ui/textarea'
import { trpc } from '@/trpc/client'

type PdfTexts = inferRouterOutputs<AppRouter>['settings']['getPdfTexts']
type PdfTextKey = keyof PdfTexts

// Mirrors MAX_PDF_TEXT_LENGTH on the server
const MAX_LENGTH = 5000

const SECTIONS: {
	title: string
	description: string
	fields: { key: PdfTextKey; label: string; rows: number }[]
}[] = [
	{
		title: 'Dotazník',
		description: 'Zdravotní dotazník, který pacient vyplňuje a podepisuje na tabletu',
		fields: [
			{ key: 'dot_header', label: 'Hlavička', rows: 4 },
			{ key: 'dot_footer', label: 'Patička', rows: 14 },
		],
	},
	{
		title: 'Objednací list',
		description: 'Seznam objednaných termínů s cenovým rozpětím',
		fields: [
			{ key: 'obj_header', label: 'Hlavička', rows: 5 },
			{ key: 'obj_footer', label: 'Patička', rows: 14 },
		],
	},
]

const cardClass = 'space-y-5 rounded-xl border border-[#e8e4df] bg-white px-5 py-5'
const primaryButton = 'gap-2 bg-[#c49b5a] text-white hover:bg-[#b38a4a]'
const ghostButton = 'text-[#6b5c4c] hover:bg-[#f5ebe0] hover:text-[#3d3428]'
const fieldClass =
	'border-[#e8e4df] bg-white text-[#3d3428] placeholder:text-[#a09689] focus:border-[#d7ad6c] focus-visible:border-[#d7ad6c]'
const hintClass = 'text-xs text-[#6b5c4c]'

function errorMessage(err: unknown, fallback: string): string {
	return err instanceof TRPCClientError ? err.message : fallback
}

function PdfTextsForm({ data }: { data: PdfTexts }) {
	const utils = trpc.useUtils()
	const [draft, setDraft] = useState<PdfTexts>(data)
	// Server data changed (save, refetch) → start from it again
	useEffect(() => setDraft(data), [data])

	const save = trpc.settings.updatePdfTexts.useMutation({
		onSuccess: () => {
			toast.success('Texty uloženy')
			utils.settings.getPdfTexts.invalidate()
		},
		onError: (err) => toast.error(errorMessage(err, 'Texty se nepodařilo uložit')),
	})
	const dirty = SECTIONS.some((section) =>
		section.fields.some(({ key }) => draft[key] !== data[key]),
	)
	const tooLong = Object.values(draft).some((value) => value.length > MAX_LENGTH)

	return (
		<>
			{SECTIONS.map((section) => (
				<div key={section.title} className={cardClass}>
					<div>
						<h2 className="text-lg font-semibold text-[#3d3428]">{section.title}</h2>
						<p className="text-sm text-[#6b5c4c]">{section.description}</p>
					</div>
					{section.fields.map(({ key, label, rows }) => (
						<div key={key} className="space-y-2">
							<div className="flex items-baseline justify-between">
								<Label htmlFor={key} className="text-[#3d3428]">
									{label}
								</Label>
								<span
									className={
										draft[key].length > MAX_LENGTH ? 'text-xs text-red-600' : hintClass
									}
								>
									{draft[key].length} / {MAX_LENGTH}
								</span>
							</div>
							<Textarea
								id={key}
								rows={rows}
								value={draft[key]}
								onChange={(event) =>
									setDraft((current) => ({ ...current, [key]: event.target.value }))
								}
								className={fieldClass}
							/>
						</div>
					))}
				</div>
			))}

			<div className="sticky bottom-0 flex flex-wrap items-center gap-3 rounded-xl border border-[#e8e4df] bg-white px-5 py-4">
				<Button
					type="button"
					onClick={() => save.mutate(draft)}
					disabled={save.isPending || !dirty || tooLong}
					className={primaryButton}
				>
					<Save className="h-4 w-4" />
					{save.isPending ? 'Ukládám…' : 'Uložit texty'}
				</Button>
				{dirty && (
					<Button
						type="button"
						variant="ghost"
						onClick={() => setDraft(data)}
						className={ghostButton}
					>
						Zahodit změny
					</Button>
				)}
				<span className={hintClass}>
					Změny se projeví u nově vytvořených PDF, už uložené dokumenty zůstanou beze změny.
				</span>
			</div>
		</>
	)
}

export function PdfTextsSettingsPage() {
	const navigate = useNavigate()
	const texts = trpc.settings.getPdfTexts.useQuery()

	return (
		<div className="min-h-screen bg-[#faf8f5]">
			<div className="mx-auto max-w-3xl space-y-6 px-6 py-6">
				<div className="flex items-center justify-between">
					<button
						type="button"
						onClick={() => navigate(-1)}
						className="inline-flex items-center gap-2 text-[#5c5142] transition-colors hover:text-[#3d3428]"
					>
						<ArrowLeft className="h-4 w-4" />
						<span>Zpět</span>
					</button>
					<h1 className="text-2xl font-semibold text-[#3d3428]">Texty v PDF</h1>
					<div className="w-16" />
				</div>

				{texts.data ? (
					<PdfTextsForm data={texts.data} />
				) : texts.isError ? (
					<div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
						Texty se nepodařilo načíst.
					</div>
				) : (
					<div className="flex justify-center py-8">
						<LoadingSpinner />
					</div>
				)}
			</div>
		</div>
	)
}

export default PdfTextsSettingsPage
